/**
 * @fileoverview Mock-only state behind the Active Clients endpoints.
 * @description The shared Tenant store has no place for what the backend's Active Clients routes add:
 * whether a client is archived (`INACTIVE`) and when, the version (`tp1.` ETag) an edit must quote,
 * and the bank's first user (the HR Admin) with the state of their invitation. This module keeps
 * those in memory, keyed by tenant id, so active-clients-mock-projection.ts can project them into
 * rows and details and active-clients-mock.service.ts can change them. An HR Admin that was never
 * invited through these routes is derived from the tenant's onboarding flags. Nothing is persisted: a
 * page reload resets it, like the rest of the mock stores. A no-op for the real backend, which never
 * loads it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { Tenant } from '@/features/sales/types/tenant.types'

/** @description The pause the backend enforces between two resends of one invitation. */
export const RESEND_COOLDOWN_SECONDS = 30

/** @description The most times one invitation may be resent. */
export const RESEND_LIMIT = 5

/** @description How long an invitation stays valid. */
export const INVITATION_VALID_DAYS = 7

const DAY_MS = 24 * 60 * 60 * 1000

/** @description The latest invitation of a mock HR Admin. */
export interface MockInvitation {
  invitationId: string
  sentAt: string
  lastSentAt: string
  expiresAt: string
  resendCount: number
}

/** @description The bank's first user in the mock. */
export interface MockHrAdmin {
  userId: string
  name: string
  email: string
  phone: string
  invitedById: string
  invitedAt: string
  invitationsSent: number
  /** Null once the HR Admin has accepted and is active. */
  invitation: MockInvitation | null
  active: boolean
}

/** @description When each archived client was archived, by tenant id. */
const inactivatedAt = new Map<string, string>()

/** @description How many times each client has been changed (edit, archive, restore, invite). */
const revisions = new Map<string, number>()

/** @description The HR Admins set through the routes, by tenant id; absent means derive it. */
const hrAdmins = new Map<string, MockHrAdmin>()

/**
 * @description When an archived client was archived.
 * @param {string} tenantId - The client.
 * @returns {string | null} The time, or null when the client is active.
 */
export function inactivatedAtOf(tenantId: string): string | null {
  return inactivatedAt.get(tenantId) ?? null
}

/**
 * @description Archives a client.
 * @param {string} tenantId - The client.
 * @returns {string} When it was archived.
 */
export function inactivate(tenantId: string): string {
  const at = new Date().toISOString()
  inactivatedAt.set(tenantId, at)
  bumpRevision(tenantId)
  return at
}

/**
 * @description Restores an archived client.
 * @param {string} tenantId - The client.
 */
export function reactivate(tenantId: string): void {
  inactivatedAt.delete(tenantId)
  bumpRevision(tenantId)
}

/**
 * @description How many times a client has been changed.
 * @param {string} tenantId - The client.
 * @returns {number} Its revision (0 until first changed).
 */
export function revisionOf(tenantId: string): number {
  return revisions.get(tenantId) ?? 0
}

/**
 * @description Records that a command changed a client, so its version moves on.
 * @param {string} tenantId - The client.
 */
export function bumpRevision(tenantId: string): void {
  revisions.set(tenantId, revisionOf(tenantId) + 1)
}

/**
 * @description The HR Admin the onboarding flags imply for a client nobody has invited through the
 * routes: none when the bank was never invited, a pending invitation while its profile is
 * incomplete, an active user once it is complete.
 * @param {Tenant} tenant - The client's tenant record.
 * @returns {MockHrAdmin | null} The derived HR Admin.
 */
function derivedHrAdmin(tenant: Tenant): MockHrAdmin | null {
  if (!tenant.onboarding?.hrAdminInvited) return null
  const now = Date.now()
  const active = tenant.onboarding.profileComplete
  return {
    userId: `HRA-${tenant.id}`,
    name: tenant.authorisedContact.name,
    email: tenant.authorisedContact.email,
    phone: tenant.authorisedContact.phone,
    invitedById: tenant.currentOwnerId ?? '',
    invitedAt: tenant.clientSince ?? tenant.createdAt,
    invitationsSent: 1,
    active,
    invitation: active
      ? null
      : {
          invitationId: `INV-${tenant.id}`,
          sentAt: new Date(now - DAY_MS).toISOString(),
          lastSentAt: new Date(now - DAY_MS).toISOString(),
          expiresAt: new Date(now + (INVITATION_VALID_DAYS - 1) * DAY_MS).toISOString(),
          resendCount: 0,
        },
  }
}

/**
 * @description A client's HR Admin: the one set through the routes, else the one its onboarding
 * flags imply.
 * @param {Tenant} tenant - The client's tenant record.
 * @returns {MockHrAdmin | null} The HR Admin, or null when the bank was never invited.
 */
export function hrAdminOf(tenant: Tenant): MockHrAdmin | null {
  return hrAdmins.get(tenant.id) ?? derivedHrAdmin(tenant)
}

/**
 * @description Stores a client's HR Admin after an invite or a resend, and moves its version on.
 * @param {string} tenantId - The client.
 * @param {MockHrAdmin} hrAdmin - The HR Admin as it is now.
 */
export function saveHrAdmin(tenantId: string, hrAdmin: MockHrAdmin): void {
  hrAdmins.set(tenantId, hrAdmin)
  bumpRevision(tenantId)
}

/**
 * @description Whether the HR Admin's invitation is still valid (pending and unexpired).
 * @param {MockHrAdmin | null} hrAdmin - The HR Admin.
 * @returns {boolean} True for a pending, unexpired invitation.
 */
export function hasPendingInvitation(hrAdmin: MockHrAdmin | null): boolean {
  return (
    hrAdmin !== null &&
    !hrAdmin.active &&
    hrAdmin.invitation !== null &&
    Date.parse(hrAdmin.invitation.expiresAt) > Date.now()
  )
}
